import React, { useState, useEffect, useCallback } from 'react';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Calculator } from 'lucide-react';

// Parse and calculate mathematical expressions in text — deterministic
// recursive-descent evaluator (no `eval` / `new Function` / dynamic code).
// Grammar:  Expr → Term (('+'|'-') Term)*
//           Term → Factor (('*'|'/') Factor)*
//           Factor → NUMBER '%'? | '(' Expr ')'
const _tokenize = (s) => {
  const toks = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i += 1; continue; }
    if ('+-*/()%'.includes(c)) { toks.push({ t: c }); i += 1; continue; }
    if (/[\d.]/.test(c)) {
      let j = i;
      while (j < s.length && /[\d.]/.test(s[j])) j += 1;
      const n = Number(s.slice(i, j));
      if (!Number.isFinite(n)) throw new Error('bad number');
      toks.push({ t: 'n', v: n });
      i = j;
      continue;
    }
    throw new Error('bad char');
  }
  return toks;
};

const _evalTokens = (toks) => {
  let pos = 0;
  const peek = () => toks[pos];
  const eat = (t) => {
    const cur = toks[pos];
    if (!cur || cur.t !== t) throw new Error('expected ' + t);
    pos += 1;
    return cur;
  };
  const parseFactor = () => {
    const cur = peek();
    if (!cur) throw new Error('unexpected end');
    let val;
    if (cur.t === '(') { eat('('); val = parseExpr(); eat(')'); }
    else if (cur.t === 'n') { val = eat('n').v; }
    else if (cur.t === '-') { eat('-'); val = -parseFactor(); }
    else if (cur.t === '+') { eat('+'); val = parseFactor(); }
    else throw new Error('unexpected token ' + cur.t);
    if (peek() && peek().t === '%') { eat('%'); val = val / 100; }
    return val;
  };
  const parseTerm = () => {
    let v = parseFactor();
    while (peek() && (peek().t === '*' || peek().t === '/')) {
      const op = eat(peek().t).t;
      const rhs = parseFactor();
      v = op === '*' ? v * rhs : v / rhs;
    }
    return v;
  };
  const parseExpr = () => {
    let v = parseTerm();
    while (peek() && (peek().t === '+' || peek().t === '-')) {
      const op = eat(peek().t).t;
      const rhs = parseTerm();
      v = op === '+' ? v + rhs : v - rhs;
    }
    return v;
  };
  const val = parseExpr();
  if (pos < toks.length) throw new Error('trailing tokens');
  return val;
};

const calculateExpression = (expr) => {
  try {
    const cleaned = expr.replace(/\s/g, '');
    // Only allow numbers, operators, parentheses, and % sign
    if (!/^[\d+\-*/%().]+$/.test(cleaned)) return null;
    const result = _evalTokens(_tokenize(cleaned));
    if (typeof result === 'number' && isFinite(result)) {
      return Math.round(result * 100) / 100;
    }
    return null;
  } catch {
    return null;
  }
};

// Find all mathematical expressions in text
const findExpressions = (text) => {
  const expressions = [];
  // Match patterns like: 100+200, 1000-500, 500*2, 1000/4, 1000*10%, 100+20%
  const regex = /(\d+(?:\.\d+)?(?:\s*[+\-*/]\s*\d+(?:\.\d+)?%?)+)/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    const expr = match[1];
    const result = calculateExpression(expr);
    if (result !== null) {
      expressions.push({
        expression: expr,
        result,
        index: match.index
      });
    }
  }
  
  return expressions;
};

export default function SmartNotes({ 
  value, 
  onChange, 
  placeholder = "Add notes... (Math expressions like 100+200 or 1000*10% will be calculated)",
  className = "",
  rows = 3,
  ...props 
}) {
  const [calculations, setCalculations] = useState([]);

  // Debounce the calculation
  const updateCalculations = useCallback((text) => {
    if (!text) {
      setCalculations([]);
      return;
    }
    const exprs = findExpressions(text);
    setCalculations(exprs);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateCalculations(value);
    }, 300);
    return () => clearTimeout(timer);
  }, [value, updateCalculations]);

  return (
    <div className="space-y-2">
      <Textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        rows={rows}
        {...props}
      />
      {calculations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {calculations.map((calc, index) => (
            <Badge 
              key={index} 
              variant="secondary" 
              className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
            >
              <Calculator className="h-3 w-3" />
              {calc.expression} = <span className="font-bold">${calc.result.toLocaleString()}</span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
